import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useCallback, useEffect, useMemo, } from "react";
import { AuthConfigContext } from "./config";
import { AuthApiClient } from "./auth-client";
const AuthContext = createContext(null);
export function AuthProvider({ config, children }) {
    const authClient = useMemo(() => new AuthApiClient(config.authUrl), [config.authUrl]);
    const [state, setState] = useState({
        bootstrapped: false,
        isAuthenticated: false,
        sessionToken: null,
        email: null,
        requiresSetup: false,
    });
    const handleRefreshFailed = useCallback(() => {
        authClient.cancelRefresh();
        setState((s) => ({
            ...s,
            bootstrapped: true,
            isAuthenticated: false,
            sessionToken: null,
            email: null,
            requiresSetup: false,
        }));
    }, [authClient]);
    // On mount, probe /refresh to determine whether the HttpOnly refresh cookie
    // is still valid. Success → authenticated and schedule proactive refresh.
    // Failure → not authenticated (cookie absent or expired).
    useEffect(() => {
        let cancelled = false;
        authClient
            .bootstrap()
            .then((expiresIn) => {
            if (cancelled)
                return;
            authClient.recordExpiry(expiresIn);
            authClient.scheduleRefresh(handleRefreshFailed);
            setState((s) => ({
                ...s,
                bootstrapped: true,
                isAuthenticated: true,
            }));
        })
            .catch(() => {
            if (cancelled)
                return;
            setState((s) => ({ ...s, bootstrapped: true, isAuthenticated: false }));
        });
        return () => {
            cancelled = true;
            authClient.cancelRefresh();
        };
    }, [authClient, handleRefreshFailed]);
    const login = useCallback((expiresIn) => {
        authClient.recordExpiry(expiresIn);
        setState((s) => ({ ...s, isAuthenticated: true, sessionToken: null }));
        authClient.scheduleRefresh(handleRefreshFailed);
    }, [authClient, handleRefreshFailed]);
    const setSessionToken = useCallback((token) => {
        setState((s) => ({ ...s, sessionToken: token }));
    }, []);
    const setEmail = useCallback((email) => {
        setState((s) => ({ ...s, email }));
    }, []);
    const setRequiresSetup = useCallback((v) => {
        setState((s) => ({ ...s, requiresSetup: v }));
    }, []);
    const logout = useCallback(() => {
        authClient.cancelRefresh();
        void authClient.logout();
        setState({
            bootstrapped: true,
            isAuthenticated: false,
            sessionToken: null,
            email: null,
            requiresSetup: false,
        });
    }, [authClient]);
    const value = useMemo(() => ({
        ...state,
        login,
        setSessionToken,
        setEmail,
        setRequiresSetup,
        logout,
        authClient,
    }), [
        state,
        login,
        setSessionToken,
        setEmail,
        setRequiresSetup,
        logout,
        authClient,
    ]);
    return (_jsx(AuthConfigContext.Provider, { value: config, children: _jsx(AuthContext.Provider, { value: value, children: children }) }));
}
export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx)
        throw new Error("useAuth must be used within AuthProvider");
    return ctx;
}
//# sourceMappingURL=provider.js.map